<template>
  <div class="entity host">
    <sui-form-fields v-if="visibleFields.provider">
      <EntityDropdown ref="provider" :options="providersList" v-model="entity.provider" fieldName="provider" v-if="visibleFields.provider" />
      <sui-form-field :width="2">
        <label style="opacity: 0">.</label>
        <div class="ui tiny fluid compact icon button" :class="{disabled: !access.providerCreate}" data-inverted data-position="left center" data-tooltip="Add new provider" data-goal="HOST_FORM_NEW_PROVIDER" @click.prevent="$modals.createProvider.open(null, onCreateProvider)">
          <i class="ui large fitted icons">
            <i class="cloud fitted icon" />
            <i class="inverted corner plus fitted icon" />
          </i>
        </div>
      </sui-form-field>
    </sui-form-fields>
    <sui-form-fields v-if="visibleFields.name || visibleFields.amt">
      <HostName ref="name" v-model="entity.name" v-autoselect fieldName="name" :externalError="nameError" :loading="checkingName" v-if="visibleFields.name" />
      <EntityInput ref="amt" type="number" v-model="entity.amt" fieldName="amt" v-if="visibleFields.amt" />
      <EntityInput ref="hours" type="number" v-model="entity.hours" fieldName="hours" v-if="visibleFields.hours" />
    </sui-form-fields>
    <sui-form-fields v-if="visibleFields.region || visibleFields.instance">
      <EntityDropdown ref="region" :options="regionsList" v-model="entity.region" fieldName="region" v-if="visibleFields.region" />
      <EntityDropdown ref="instance" :options="instancesList" v-model="entity.instance" fieldName="instance" v-if="visibleFields.instance" />
    </sui-form-fields>
    <sui-form-fields v-if="visibleFields.keys">
      <EntityDropdown ref="keys" :options="keysList" v-model="entity.keys" fieldName="keys" />
      <sui-form-field :width="2">
        <label style="opacity: 0">.</label>
        <div class="ui tiny fluid compact icon button" :class="{disabled: !access.keyCreate}" data-inverted data-position="left center" data-tooltip="Add new key" data-goal="HOST_FORM_NEW_KEY" @click.prevent="$modals.createKey.open({}, onCreateKey)">
          <i class="ui large fitted icons">
            <i class="key fitted icon" />
            <i class="inverted corner plus fitted icon" />
          </i>
        </div>
      </sui-form-field>
    </sui-form-fields>
    <sui-form-fields v-if="visibleFields.labels || visibleFields.backup">
      <EntityDropdown ref="labels" :options="labelsList" v-model="entity.labels" fieldName="labels" v-if="visibleFields.labels" />
      <EntityButtons :width="4" v-model="entity.backup" fieldName="backup" v-if="visibleFields.backup" />
    </sui-form-fields>
  </div>
</template>

<script>
  import debounce from 'lodash/debounce'
  import {mapGetters} from 'vuex'
  import EntityButtons from '@/components/forms/EntityButtons'
  import EntityDropdown from '@/components/forms/EntityDropdown'
  import EntityInput from '@/components/forms/EntityInput'
  import HostName from '@/components/forms/HostName'
  import FormEntity from '@/mixins/FormEntity'
  import api from '@/api'
  import objects from '@/objects'

  export default {
    name: 'FormHost',
    mixins: [
      FormEntity,
    ],
    components: {
      EntityButtons,
      EntityDropdown,
      EntityInput,
      HostName,
    },
    data() {
      return {
        object: 'Host',
        commonFields: ['amt', 'keys', 'labels', 'name', 'provider'],
        nameError: null,
        checkingName: false,
      }
    },
    computed: {
      ...mapGetters(['access', 'hostLabels', 'keys', 'providers', 'providersById']),
      providerClass() {
        return this.entity && this.entity.provider ? this.entity._provider.constructor : objects._providersByType[this.entity.providerType]
      },
      instancesList() {
        if (!this.providerClass) {
          return []
        }
        const instances = this.providerClass ? this.providerClass._instances || [] : []
        return instances.filter(instance => instance.active).map(instance => {
          return {
            icon: 'grey microchip',
            text: instance._fullTitle,
            value: instance.id,
          }
        })
      },
      keysList() {
        return this.keys.map(key => {
          return {
            icon: key.type === 'sftp' ? 'folder' : 'key',
            text:  key.name,
            value: key.id,
          }
        })
      },
      labelsList() {
        return this.hostLabels.map(label => {
          return {
            icon: 'tag',
            text:  label,
            value: label,
          }
        })
      },
      providersList() {
        return this.providers.filter(provider => provider.purpose === 'hosting').map(provider => {
          return {
            image: {src: provider._logo},
            text: provider.name,
            value: provider.id,
          }
        })
      },
      regionsList() {
        const regions = this.providerClass ? this.providerClass._regions || [] : []
        return regions.filter(region => region.active).map(region => {
          return {
            flag: region.country,
            text: region.city,
            value: region.id,
          }
        })
      },
    },
    watch: {
      'entity.name'(newName) {
        this.nameError = null
        if (newName && this.mode === 'creating') {
          this.checkingName = true
          this.debouncedCheckName(newName)
        }
      },
      'entity.provider'(newId, oldId) {
        const newProvider = this.providersById[newId] || {}
        const oldProvider = this.providersById[oldId] || {}
        // if there's an old entity and its provider type has changed
        if (oldId && newProvider.type !== oldProvider.type) {
          this.createEntity({
            region: this.regionsList[0] ? this.regionsList[0].value : null,
            instance: this.instancesList[0] ? this.instancesList[0].value : null,
          })
        }
      },
    },
    methods: {
      onCreateProvider(result) {
        if (result && result.id) {
          this.entity.provider = result.id
        }
      },
      onCreateKey(result) {
        if (result && result.id) {
          this.entity.keys.push(result.id)
        }
      },
      debouncedCheckName: debounce(function checkName(newName) {
        api.checkHostName(newName).then(number => {
          if (number > 1) {
            this.nameError = 'This name is already used'
          }
        }).finally(() => {
          this.checkingName = false
        })
      }, 500, {leading: false})
    },
    created() {
      this.createEntity({
        region: this.regionsList[0] ? this.regionsList[0].value : null,
        instance: this.instancesList[0] ? this.instancesList[0].value : null,
        ...this.$attrs.value,
      })
    },
  }
</script>

<style>
  .dropdown .item {
    white-space: nowrap!important;
  }
</style>
